import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Settings, Sparkles, Plug } from 'lucide-react';
import { toast } from 'sonner';
import SubscriptionPaywall from '@/components/SubscriptionPaywall';
import SellerProfileEditor from '@/components/SellerProfileEditor';
import FiscalFields from '@/components/FiscalFields';
import BetaBanner from '@/components/BetaBanner';
import { usePaymentsConfig } from '@/lib/usePaymentsConfig';
import { openCookieSettings } from '@/components/CookieBanner';
import PushSettings from '@/components/PushSettings';
import MobileQuickActions from '@/components/MobileQuickActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_LABELS = {
  daily: 'Diaria (24h)', trial: 'Prueba gratuita',
  monthly: 'Mensual', quarterly: 'Trimestral', annual: 'Anual'
};

const SettingsButton = () => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    postal_code: '',
    work_radius_km: 50,
    has_fleet: true,
    country: 'ES',
    region: '',
    tax_country: 'ES',
    vat_number: '',
    currency: 'EUR'
  });
  const [subOpen, setSubOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        postal_code: user.postal_code || '',
        work_radius_km: user.work_radius_km || 50,
        has_fleet: user.has_fleet !== false,
        country: user.country || 'ES',
        region: user.region || '',
        tax_country: user.tax_country || user.country || 'ES',
        vat_number: user.vat_number || '',
        currency: user.currency || 'EUR'
      });
    }
  }, [user, open]);

  const handleSave = async () => {
    if (form.country === 'ES' && !/^\d{5}$/.test(form.postal_code)) {
      toast.error('Introduce un código postal válido (5 dígitos)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        postal_code: form.postal_code,
        work_radius_km: parseInt(form.work_radius_km),
        country: form.country,
        region: form.region || '',
        tax_country: form.tax_country || form.country,
        vat_number: form.vat_number || '',
        currency: form.currency
      };
      // Only send has_fleet for suppliers
      if (user?.role === 'supplier') {
        payload.has_fleet = form.has_fleet;
      }
      await axios.patch(
        `${API}/users/me`,
        payload,
        { headers: { Authorization: getAuthHeader() } }
      );
      await refreshUser();
      toast.success('Preferencias guardadas');
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSub = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/subscriptions/cancel`, {}, {
        headers: { Authorization: getAuthHeader() }
      });
      await refreshUser();
      if (res.data.cancel_at_period_end && res.data.expires_at) {
        toast.success(`Suscripción cancelada. Mantienes el acceso hasta el ${new Date(res.data.expires_at).toLocaleDateString('es-ES')}.`);
      } else {
        toast.success('Suscripción cancelada');
      }
    } catch (error) {
      toast.error('Error al cancelar');
    } finally {
      setCancelling(false);
    }
  };

  const payCfg = usePaymentsConfig();
  const subActive = user?.subscription_type && user.subscription_type !== 'none' && user?.subscription_expires_at && new Date(user.subscription_expires_at) > new Date();
  // Buyers (restaurant / shop) use the platform 100% free — no subscription UI for them
  const isSeller = user?.role === 'supplier' || user?.role === 'manufacturer';

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="ghost"
        size="sm"
        className="text-slate-600 hover:text-slate-900"
        data-testid="settings-button"
      >
        <Settings size={20} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="settings-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajustes</DialogTitle>
            <DialogDescription>{isSeller ? 'Zona de trabajo y suscripción' : 'Zona de trabajo'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <MobileQuickActions onClose={() => setOpen(false)} />
            {/* Integrations shortcut */}
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              data-testid="open-integrations"
              onClick={() => { setOpen(false); navigate('/integraciones'); }}
            >
              <Plug size={16} /> Integraciones y API
            </Button>

            {/* Zone */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-900">Zona de trabajo</h3>
              <div className="space-y-2">
                <Label>Código Postal</Label>
                <Input
                  data-testid="settings-postal-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, '') })}
                  placeholder="08001"
                />
              </div>
              <div className="space-y-2">
                <Label>Radio de trabajo: <span className="text-emerald-600 font-bold">{form.work_radius_km} km</span></Label>
                <input
                  type="range"
                  min={5}
                  max={500}
                  step={5}
                  value={form.work_radius_km}
                  onChange={(e) => setForm({ ...form, work_radius_km: e.target.value })}
                  className="w-full accent-emerald-600"
                  data-testid="settings-radius-slider"
                />
                <div className="flex justify-between text-xs text-slate-400">
                  <span>5 km</span><span>250 km</span><span>500 km</span>
                </div>
              </div>

              {user?.role === 'supplier' && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium text-slate-900">🚚 Tengo flota de reparto</Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Si lo desactivas, solo verás subastas de recogida o flexibles.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={form.has_fleet}
                      data-testid="has-fleet-toggle"
                      onClick={() => setForm({ ...form, has_fleet: !form.has_fleet })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        form.has_fleet ? 'bg-emerald-600' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          form.has_fleet ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
              <FiscalFields idPrefix="settings" value={form} onChange={(partial) => setForm((f) => ({ ...f, ...partial }))} />
              {user?.vat_number && (
                <p className={`text-xs ${user.vat_valid ? 'text-emerald-700' : user.vat_valid === false ? 'text-red-600' : 'text-slate-500'}`} data-testid="settings-vat-status">
                  {user.vat_valid ? '✓ IVA intracomunitario validado (VIES)' : user.vat_valid === false ? '✕ IVA no válido en VIES' : 'IVA pendiente de validar'}
                </p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <button type="button" onClick={openCookieSettings} className="text-xs text-emerald-700 underline" data-testid="settings-manage-cookies">Gestionar cookies / privacidad (RGPD)</button>
                <button type="button" onClick={() => { setOpen(false); navigate('/eliminar-cuenta'); }} className="text-xs text-red-600 underline" data-testid="settings-delete-account-link">Eliminar cuenta y datos</button>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="settings-save-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? 'Guardando...' : 'Guardar preferencias'}
              </Button>
            </div>

            <div className="border-t border-slate-200"></div>
            <PushSettings />

            {isSeller && <div className="border-t border-slate-200"></div>}

            {/* Public seller profile — only for sellers */}
            {isSeller && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-900">Perfil público</h3>
                <p className="text-xs text-slate-500 leading-snug">
                  Añade descripción, horario, sitio web y logo para que los compradores te conozcan.
                </p>
                <Button
                  variant="outline"
                  onClick={() => { setOpen(false); setProfileEditorOpen(true); }}
                  data-testid="edit-public-profile-button"
                  className="w-full"
                >
                  Editar mi perfil público
                </Button>
                <a
                  href={`/proveedor/${user?.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="view-public-profile-link"
                  className="block text-center text-xs text-emerald-700 hover:underline"
                >
                  Ver mi perfil como lo verán los compradores →
                </a>
              </div>
            )}

            {isSeller && <div className="border-t border-slate-200"></div>}

            {/* Subscription — only for sellers. Buyers use the app 100% free. */}
            {isSeller && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles size={16} className="text-emerald-600" />
                Suscripción
              </h3>

              {!payCfg.payments_enabled ? (
                <div className="space-y-2">
                  <BetaBanner />
                  {payCfg.test_payments_allowed && user?.is_admin && (
                    <Button onClick={() => { setOpen(false); setSubOpen(true); }} data-testid="open-subscription-button" variant="ghost" size="sm" className="w-full text-xs text-slate-400">Stripe modo test (solo admin)</Button>
                  )}
                </div>
              ) : subActive ? (
                <div className="space-y-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="active-subscription-info">
                    <p className="text-sm font-medium text-emerald-900">
                      Plan {PLAN_LABELS[user.subscription_type] || user.subscription_type} activo
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      {user.will_renew === false ? 'Cancelada — acceso hasta:' : 'Renovación:'} {new Date(user.subscription_expires_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-emerald-700 mt-2">
                      Acceso completo a subastas y pujas mientras esté activa.
                    </p>
                  </div>
                  {user.will_renew !== false && (
                    <Button
                      onClick={handleCancelSub}
                      disabled={cancelling}
                      variant="outline"
                      data-testid="cancel-subscription-button"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    >
                      {cancelling ? 'Cancelando…' : 'Cancelar suscripción'}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    Suscríbete para pujar en subastas ilimitadas.
                  </p>
                  <Button
                    onClick={() => { setOpen(false); setSubOpen(true); }}
                    data-testid="open-subscription-button"
                    variant="outline"
                    className="w-full"
                  >
                    Ver planes de suscripción
                  </Button>
                </>
              )}
            </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription selection dialog — delegates to the shared paywall UI */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent data-testid="subscription-dialog" className="max-w-md p-0 overflow-hidden">
          <SubscriptionPaywall onSubscribed={() => setSubOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Public seller profile editor */}
      <SellerProfileEditor open={profileEditorOpen} onOpenChange={setProfileEditorOpen} />
    </>
  );
};

export default SettingsButton;
